import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Gavel, XCircle, ShieldAlert, FileText, CalendarDays, ListChecks, Wallet, Coins, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  gavel: Gavel,
  "x-circle": XCircle,
  shield: ShieldAlert,
  "file-text": FileText,
  "calendar-days": CalendarDays,
  "list-checks": ListChecks,
  wallet: Wallet,
  coins: Coins,
  "alert-circle": AlertCircle,
  bell: Bell,
};

const BADGES = {
  info:    { chip: "bg-[#e0f2fe] text-[#0c4a6e] border-[#bae6fd]" },
  warn:    { chip: "bg-[#fef3c7] text-[#8b6f2a] border-[#fde68a]" },
  danger:  { chip: "bg-[#fee2e2] text-[#991b1b] border-[#fecaca]" },
  success: { chip: "bg-[#dcfce7] text-[#166534] border-[#bbf7d0]" },
};

/**
 * Role-aware notification bell — pulls /api/notifications and renders each
 * item as a clickable card that deep-links to its destination.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch { /* ignore transient errors — the bell must never crash the Topbar */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);   // refresh every 60 seconds
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const count = items.length;
  const hasDot = count > 0;

  const groups = useMemo(() => {
    const priority = { danger: 0, warn: 1, info: 2, success: 3 };
    const sorted = [...items].sort(
      (a, b) => (priority[a.badge] ?? 9) - (priority[b.badge] ?? 9),
    );
    return sorted;
  }, [items]);

  const handleClick = (item) => {
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <div ref={rootRef} className="relative">
      <button onClick={() => { setOpen((v) => !v); if (!open) load(); }}
              className="relative p-2 rounded-md hover:bg-[#f5f4f0]"
              data-testid="notification-bell"
              title={count === 0 ? "No new notifications" : `${count} notification(s)`}>
        <Bell size={16} className="text-[#475569]" />
        {hasDot ? (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                style={{ background: "#c4a454" }}
                data-testid="notification-badge">
            {count > 9 ? "9+" : count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="absolute right-0 mt-1 w-[380px] rounded-lg shadow-2xl bg-white border border-[#e2e1dd] overflow-hidden z-50"
             data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-[#e2e1dd] bg-[#faf9f5] flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#c4a454]">Notifications</div>
              <div className="text-sm font-serif text-[#0b192c]">
                {count === 0 ? "All caught up." : `${count} item${count === 1 ? "" : "s"} awaiting you`}
              </div>
            </div>
            <button onClick={load} disabled={loading}
                    className="p-1 rounded hover:bg-white transition"
                    title="Refresh" data-testid="notification-refresh">
              <RefreshCw size={12} className={`text-[#475569] ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {count === 0 ? (
              <div className="p-6 text-center text-sm text-[#475569]"
                   data-testid="notification-empty">
                <CheckCircle2 size={22} className="mx-auto mb-2 text-[#166534]" />
                Nothing to do right now. Nice.
              </div>
            ) : (
              groups.map((n) => {
                const Icon = ICONS[n.icon] || Bell;
                const chip = (BADGES[n.badge] || BADGES.info).chip;
                return (
                  <button key={n.id} onClick={() => handleClick(n)}
                          className="w-full text-left px-4 py-3 border-b last:border-b-0 border-[#f0efe9] hover:bg-[#faf9f5] transition flex items-start gap-3"
                          data-testid={`notification-item-${n.id}`}>
                    <span className={`w-8 h-8 flex-shrink-0 rounded flex items-center justify-center border ${chip}`}>
                      <Icon size={14} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#0b192c] truncate">{n.title}</div>
                      <div className="text-[11px] text-[#475569] mt-0.5 line-clamp-2">{n.subtitle}</div>
                      {n.at_relative ? (
                        <div className="text-[9px] uppercase tracking-widest font-semibold text-[#94a3b8] mt-1">
                          {n.at_relative}
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
